import { useState, useMemo, useEffect } from 'react';
import { Modal, DefaultButton, PrimaryButton, Checkbox, FontIcon, Spinner, SpinnerSize, TextField } from '@fluentui/react';
import { FileWithCandidateDetails } from '../../types/FileWithCandidateDetails';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import styles from './CandidateReportModal.module.scss';
import { extractCandidateFirstName, extractCandidateLastName} from '../../utilities/nameExtractor';
import { extractCandidateNameAI } from '../../api/api';
import { useAuthErrorContext } from '../../components/AuthErrorProvider';
import clsx from 'clsx';

const compressImage = (file: File, maxWidth = 800): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (maxWidth * height) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Could not compress image'));
              return;
            }
            // Create a new file with .jpg extension
            const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          0.8, // compression quality (0.8 = 80%)
        );
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
};

type FieldKey = keyof FileWithCandidateDetails;

function CandidateReportModal({
  files,
  isOpen,
  setIsOpen,
  onUpload,
}: {
  files: File[];
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  onUpload: (filesWithCandidateDetails: FileWithCandidateDetails[]) => Promise<void>;
}) {
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [data, setData] = useState<FileWithCandidateDetails[]>([]);
  const [currentData, setCurrentData] = useState<FileWithCandidateDetails>({} as FileWithCandidateDetails);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isExtractingName, setIsExtractingName] = useState(false);
  const [fullName, setFullName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleAuthError } = useAuthErrorContext();
  const intl = useIntl();

  const getInitialData = (file: File): FileWithCandidateDetails => ({
    file,
    jobDescription: '',
    candidateFirstName: extractCandidateFirstName(file?.name),
    candidateLastName: extractCandidateLastName(file?.name),
    positionName: '',
    consultantName: '',
    consultantEmail: '',
    consultantPhone: '',
    candidateTitle: '',
    currentRemuneration: '',
    desiredRemuneration: '',
    noticePeriod: '4 Weeks',
    upcomingLeave: 'None',
    withoutAssessmentCriteria: false,
    companyApplied: '',
    candidateRecentEmployer: '',
    jobNumber: '',
    other: 'None',
  } as FileWithCandidateDetails);

  const isSubmitDisabled = useMemo(() => {
    const requiredFields: FieldKey[] = [
      'candidateFirstName',
      'candidateLastName',
      'positionName',
      'consultantName',
      'consultantEmail',
      'consultantPhone',
      'candidateTitle',
      'currentRemuneration',
      'desiredRemuneration',
      'noticePeriod',
      'upcomingLeave',
      'companyApplied',
      'candidateRecentEmployer',
      'jobNumber',
    ];

    const isJobDescriptionRequired =
      !currentData.withoutAssessmentCriteria && !currentData.jobDescription?.toString().trim();

    return (
      currentFileIndex !== files.length - 1 ||
      isJobDescriptionRequired ||
      requiredFields.some(field => !currentData[field]?.toString().trim())
    );
  }, [currentFileIndex, files, currentData]);

  const handleAINameExtraction = async () => {
    try {
      setIsExtractingName(true);
      const response = await extractCandidateNameAI({ filename: files[currentFileIndex].name }, handleAuthError);
      const extractedName = response?.data?.name;
      if (extractedName) {
        setFullName(extractedName);
        const nameParts = extractedName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        setCurrentData(prev => ({ ...prev, candidateFirstName: firstName, candidateLastName: lastName }));
        
        toast.success(intl.formatMessage({ id: 'modal.candidateReport.nameExtracted' }));
      }else{
        toast.warning("Could not extract name, name was not findable within the filename");
      }
    } catch (error) {
      console.error('Error extracting name:', error);
      toast.error(intl.formatMessage({ id: 'modal.candidateReport.nameExtractionError' }));
    } finally {
      setIsExtractingName(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setCurrentFileIndex(0);
      setData([]);
      setCurrentData({} as FileWithCandidateDetails);
      setFullName('');
    } else {
      setData(files.map(getInitialData));
    }
  }, [isOpen, files]);

  useEffect(() => {
    const current = data[currentFileIndex] ?? getInitialData(undefined!);
    setCurrentData(current);
    // Set the full name when changing records
    setFullName(`${current?.candidateFirstName || ''} ${current?.candidateLastName || ''}`.trim());
  }, [data, currentFileIndex]);

  useEffect(() => {
    if (currentData?.profilePhoto) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(currentData.profilePhoto);
    } else {
      setImagePreview(null);
    }
  }, [currentData?.profilePhoto]);

  const previous = () => {
    const updatedData = data.toSpliced(currentFileIndex, 1, currentData);
    const prevData = {
      ...data[currentFileIndex - 1],
      jobDescription: currentData.jobDescription,
      companyApplied: currentData.companyApplied,
      jobNumber: currentData.jobNumber,
      positionName: currentData.positionName,
      consultantName: currentData.consultantName,
      consultantEmail: currentData.consultantEmail,
      consultantPhone: currentData.consultantPhone,
    };
    setData(updatedData.toSpliced(currentFileIndex - 1, 1, prevData));
    setCurrentData(prevData);
    setCurrentFileIndex(currentFileIndex - 1);
    setImagePreview(null);
  };

  const next = () => {
    const updatedData = data.toSpliced(currentFileIndex, 1, currentData);
    const nextData = {
      ...data[currentFileIndex + 1],
      jobDescription: currentData.jobDescription,
      companyApplied: currentData.companyApplied,
      jobNumber: currentData.jobNumber,
      positionName: currentData.positionName,
      consultantName: currentData.consultantName,
      consultantEmail: currentData.consultantEmail,
      consultantPhone: currentData.consultantPhone,
    };
    setData(updatedData.toSpliced(currentFileIndex + 1, 1, nextData));
    setCurrentData(nextData);
    setCurrentFileIndex(currentFileIndex + 1);
    setImagePreview(null);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (!currentData.withoutAssessmentCriteria && !currentData.jobDescription?.trim()) {
        toast.error(intl.formatMessage({ id: 'candidateReport.error.jobDescription.required' }));
        return;
      }

      const updatedData = data.toSpliced(currentFileIndex, 1, currentData);
      const finalData = updatedData.map(item => ({
        ...item,
      }));
      setData(finalData);
      await onUpload(finalData);
      setIsOpen(false);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: 'candidateReport.error.failed.upload' }));
    } finally {
      setIsSubmitting(false);
    }

  };

  const handleClose = () => {
    setIsOpen(false);
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleClose}
      isBlocking={true}
      className={styles.CandidateReportModal}
      containerClassName={styles.noScroll}
    >
      <div className={styles.modalHeader}>
        <h2 title={files[currentFileIndex]?.name || 'No file selected'}>
          {files[currentFileIndex]?.name || 'No file selected'}
          <br />
          <small>
            ({currentFileIndex + 1}/{data.length})
          </small>
        </h2>
        <DefaultButton onClick={handleClose} iconProps={{ iconName: 'Cancel' }} />
      </div>

      <div className={styles.formContainer}>
        <div className={styles.photoSection}>
          <input
            type="file"
            accept="image/*"
            onChange={async e => {
              const file = e.target.files?.[0];
              if (file) {
                try {
                  const compressedFile = await compressImage(file);
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setImagePreview(reader.result as string);
                  };
                  reader.readAsDataURL(compressedFile);
                  setCurrentData({ ...currentData, profilePhoto: compressedFile });
                } catch (error) {
                  console.error('Error compressing image:', error);
                  toast.error(intl.formatMessage({ id: 'modal.candidateReport.imageCompressionError' }));
                }
              }
            }}
            className={styles.fileInput}
            id="profile-photo-upload"
          />
          <label htmlFor="profile-photo-upload" className={styles.uploadArea}>
            {imagePreview ? (
              <div className={styles.imageContainer}>
                <img
                  src={imagePreview}
                  alt={intl.formatMessage({ id: 'modal.candidateReport.profilePhotoPreview' })}
                  className={styles.imagePreview}
                />
                <div className={styles.imageActions}>
                  <button
                    type="button"
                    className={styles.imageAction}
                    onClick={e => {
                      e.preventDefault();
                      setImagePreview(null);
                      setCurrentData({ ...currentData, profilePhoto: undefined });
                    }}
                    title={intl.formatMessage({ id: 'modal.candidateReport.removePhoto' })}
                  >
                    <FontIcon iconName="ChromeClose" />
                  </button>
                  <label
                    htmlFor="profile-photo-upload"
                    className={styles.imageAction}
                    title={intl.formatMessage({ id: 'modal.candidateReport.changePhoto' })}
                  >
                    <FontIcon iconName="Edit" />
                  </label>
                </div>
              </div>
            ) : (
              <div className={styles.placeholderIcon}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                  />
                </svg>
                <div className={styles.uploadIcon}>
                  <FontIcon iconName="CloudUpload" />
                </div>
              </div>
            )}
          </label>
          <div className={styles.photoLabel}>
            {intl.formatMessage({ id: 'modal.candidateReport.profilePhotoLabel' })}
          </div>
        </div>

        <Checkbox
          label={intl.formatMessage({ id: 'modal.candidateReport.title' })}
          checked={currentData?.withoutAssessmentCriteria ?? false}
          onChange={(_, checked) => setCurrentData({ ...currentData, withoutAssessmentCriteria: checked ?? false })}
          className={styles.checkbox}
        />

        <div className={styles.formField}>
          <label>
            {intl.formatMessage({ id: 'modal.candidateReport.jobDescription' })}
            {!currentData?.withoutAssessmentCriteria && <span className={styles.required}>*</span>}
          </label>
          <textarea
            value={currentData?.jobDescription ?? ''}
            onChange={e => setCurrentData({ ...currentData, jobDescription: e.target.value })}
            required={!currentData?.withoutAssessmentCriteria}
            className={clsx(styles.textarea, {
              [styles.error]: !currentData?.withoutAssessmentCriteria && !currentData?.jobDescription?.trim(),
            })}
          />
        </div>

        {/* Company Details */}
        <div className={styles.formField}>
          <label>
            {intl.formatMessage({ id: 'modal.candidateReport.companyName' })}
            {<span className={styles.required}>*</span>}
          </label>
          <input
            type="text"
            value={currentData?.companyApplied ?? ''}
            onChange={e => setCurrentData({ ...currentData, companyApplied: e.target.value })}
          />
        </div>

        {/* Job Number */}
        <div className={styles.formField}>
          <label>Job Number
          {<span className={styles.required}>*</span>}
          </label>

          <input
            type="text"
            value={currentData?.jobNumber ?? ''}
            onChange={e => setCurrentData({ ...currentData, jobNumber: e.target.value })}
          />
        </div>

        {/* Candidate Details */}
        <div className={styles.formField}>
          <label>
            {intl.formatMessage({ id: 'modal.candidateReport.candidateName' })}
            {<span className={styles.required}>*</span>}
          </label>
          <div className={styles.inputWithSpinner}>
            <input
              value={fullName}
              onChange={(e) => {
                // Just update the fullName state variable
                setFullName(e.target.value || '');
                
                // Process the name for validation/submission
                const value = (e.target.value || '').trim();
                if (!value) {
                  setCurrentData({
                    ...currentData,
                    candidateFirstName: '',
                    candidateLastName: ''
                  });
                  return;
                }
                
                // Split at first space
                const firstSpaceIndex = value.indexOf(' ');
                if (firstSpaceIndex === -1) {
                  // No space found, all is first name
                  setCurrentData({
                    ...currentData,
                    candidateFirstName: value,
                    candidateLastName: ''
                  });
                } else {
                  const firstName = value.substring(0, firstSpaceIndex).trim();
                  const lastName = value.substring(firstSpaceIndex + 1).trim();
                  
                  setCurrentData({
                    ...currentData,
                    candidateFirstName: firstName,
                    candidateLastName: lastName
                  });
                }
              }}
              className={clsx({
                [styles.error]: !currentData?.candidateFirstName?.trim() || !currentData?.candidateLastName?.trim(),
              })}
              placeholder={intl.formatMessage({ id: 'modal.candidateReport.candidateName.placeholder' })}
            />
            <DefaultButton
              onClick={async () => {
                await handleAINameExtraction();
              }}
              disabled={isExtractingName}
              iconProps={{ iconName: 'TagUnknown' }}
              styles={{
                root: { minWidth: '32px', padding: '0 8px', marginLeft: '8px' }
              }}
              title={intl.formatMessage({ id: 'modal.candidateReport.extractWithAI' })}
            >
              {isExtractingName ? (
                <Spinner size={SpinnerSize.small} />
              ) : (
                intl.formatMessage({ id: 'modal.candidateReport.aiExtract' })
              )}
            </DefaultButton>
          </div>
        </div>

        {/* Location */}
        <div className={styles.formField}>
          <label>
            {intl.formatMessage({ id: 'modal.candidateReport.location' })}
            {<span className={styles.required}>*</span>}
          </label>
          <input
            type="text"
            value={currentData?.location ?? ''}
            onChange={e => setCurrentData({ ...currentData, location: e.target.value })}
            className={clsx({
              [styles.error]: !currentData?.location?.trim(),
            })}
          />
        </div>

        {/* Position Details */}
        <div className={styles.formField}>
          <label>
            {intl.formatMessage({ id: 'modal.candidateReport.positionName' })}
            {<span className={styles.required}>*</span>}
          </label>
          <input
            type="text"
            value={currentData?.positionName ?? ''}
            onChange={e => setCurrentData({ ...currentData, positionName: e.target.value })}
          />
        </div>

        {/* Additional Fields */}
        {[
          { key: 'consultantName' as FieldKey, labelId: 'modal.candidateReport.consultantName', required: true },
          { key: 'consultantEmail' as FieldKey, labelId: 'modal.candidateReport.consultantEmail', required: true },
          { key: 'consultantPhone' as FieldKey, labelId: 'modal.candidateReport.consultantPhone', required: true },
          { key: 'candidateTitle' as FieldKey, labelId: 'modal.candidateReport.currentTitle', required: true },
          {
            key: 'candidateRecentEmployer' as FieldKey,
            labelId: 'modal.candidateReport.currentEmployer',
            required: true,
          },
          {
            key: 'currentRemuneration' as FieldKey,
            labelId: 'modal.candidateReport.currentRemuneration',
            required: true,
          },
          {
            key: 'desiredRemuneration' as FieldKey,
            labelId: 'modal.candidateReport.desiredRemuneration',
            required: true,
          },
          { 
            key: 'noticePeriod' as FieldKey, 
            labelId: 'modal.candidateReport.noticePeriod', 
            placeholderId: 'modal.candidateReport.noticePeriod.placeholder',
            required: true 
          },
          { 
            key: 'upcomingLeave' as FieldKey, 
            labelId: 'modal.candidateReport.upcomingLeave', 
            placeholderId: 'modal.candidateReport.upcomingLeave.placeholder',
            required: true 
          },
          { 
            key: 'other' as FieldKey, 
            labelId: 'modal.candidateReport.other', 
            placeholderId: 'modal.candidateReport.other.placeholder',
            required: false 
          },
        ].map(field => (
          <div key={field.key} className={styles.formField}>
            <label>
              {intl.formatMessage({ id: field.labelId })}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input
              type="text"
              value={String(currentData?.[field.key] ?? '')}
              onChange={e => setCurrentData({ ...currentData, [field.key]: e.target.value })}
              required={field.required}
              placeholder={field.placeholderId ? intl.formatMessage({ id: field.placeholderId }) : undefined}
              className={clsx({
                [styles.error]: field.required && !currentData?.[field.key]?.toString().trim(),
              })}
            />
          </div>
        ))}
      </div>

      <div className={styles.modalFooter}>
        <DefaultButton
          onClick={previous}
          disabled={currentFileIndex === 0}
          text={intl.formatMessage({ id: 'modal.candidateReport.previous' })}
        />
        <DefaultButton
          onClick={next}
          disabled={currentFileIndex === files.length - 1}
          text={intl.formatMessage({ id: 'modal.candidateReport.next' })}
        />
        <div className={styles.spacer} />
        <DefaultButton onClick={handleClose} text="Cancel" />
        <PrimaryButton onClick={handleSubmit} text={isSubmitting ? intl.formatMessage({ id: 'modal.candidateReport.submitting' }) : 'Submit'} disabled={isSubmitDisabled || isSubmitting || isExtractingName} />
      </div>
    </Modal>
  );
}

export default CandidateReportModal;
